/* eslint-disable jsx-a11y/anchor-is-valid */
// @flow
import React, {useCallback, useEffect} from "react";
import PriceOverviewContainer from "../../../container/checkout/dataSummaryBox/PriceOverviewContainer";
import CartItemOverviewContainer from "../../../container/checkout/mainContentBox/OrderSummaryCartItemContainer";
import i18n from "../../../i18next";
import type {Item} from "../../../models/Item";
import type {Adress} from "../../../models/Adress";
import {PAYMENT_METHODS, PAYMENT_PROVIDER} from "../../../models/PaymentRequests";
import "./OrderSummary.scss";
import PickupLocationInfoContainer from "../../../container/pickupLocation/PickupLocationInfoContainer";
import Modal from "react-modal";
import type {PaymentFacility} from "../../../api/PaymentApi";
import {toast} from "react-toastify";
import NextButton from "../NextButton";
import {OrderState} from "../../../models/Order";
import Loading from "../../layout/Loading";
import {useTranslation} from "react-i18next";
import P24PayPalButtonContainer from "../../../container/checkout/dataSummaryBox/P24PayPalButtonContainer";
import PayPalButtonContainer from "../../../container/checkout/dataSummaryBox/PayPalButtonContainer";
import usePayPalScript from "../../../hooks/usePayPalScript";
import {emitInternalEvent} from "../../../helper/tracker/internalEvents";
import WarrantyNoticeModal from "../../empco/WarrantyNoticeModal";

interface OrderSummaryProps {
	items: Item[];
    adress: Adress;
	shippingText: string;
	withPickup: boolean;
	showPickupLocation: boolean;
	legalTerms: string;
	dealerId: string;
	notificationUrl: string;
	paymentFacility: PaymentFacility;
    paymentAuthorizationFailed: boolean;
    orderState: OrderState;
    orderNo: string;
    paymentVerified: boolean;
    getOrderState: (orderNo: string) => void;
    editBillingAddress: () => void;
    submitOrder: () => void;
    euShop: boolean;
}

const OrderSummary = (props: OrderSummaryProps) => {
    const {
        paymentAuthorizationFailed,
        adress,
        orderState,
        items,
        shippingText,
        showPickupLocation,
        withPickup,
        paymentFacility,
        legalTerms,
        notificationUrl,
        dealerId,
        orderNo,
        paymentVerified,
        getOrderState,
        submitOrder,
        euShop
    } = props;
    const {t} = useTranslation();
    const [accept, setAccept] = React.useState(false);
    const [showLegalTerms, setShowLegalTerms] = React.useState(false);
    const [showWarrantyNotice, setShowWarrantyNotice] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(paymentAuthorizationFailed);

    const {getFundingSource} = usePayPalScript();

    useEffect(() => {
        if (paymentAuthorizationFailed) {
            toast.error(t("cart.paymentApiError"));
        }
    }, [paymentAuthorizationFailed, t]);

    // excluding paypal express
    const isPayPalPayment = useCallback(() => {
        return paymentFacility.paymentMethod === PAYMENT_METHODS.PAYPAL ||
            paymentFacility.paymentMethod === PAYMENT_METHODS.CARD ||
            paymentFacility.paymentMethod === PAYMENT_METHODS.SEPA ||
            paymentFacility.paymentMethod === PAYMENT_METHODS.PRZELEWY24;
    }, [paymentFacility.paymentMethod]);

    const isPayPalExpressPayment = () => {
        return paymentFacility.paymentMethod === PAYMENT_METHODS.PAYPAL_EXPRESS;
    }

    const isPayPalProvider = useCallback(() => {
        return paymentFacility.paymentProvider === PAYMENT_PROVIDER.PAYPAL ||
            paymentFacility.paymentProvider === PAYMENT_PROVIDER.PAYPAL_CAPTURE ||
            paymentFacility.paymentProvider === PAYMENT_PROVIDER.PAYPAL_EXPRESS;
    }, [paymentFacility.paymentProvider]);

    const isOrderPending = useCallback(() => {
        return orderNo && paymentVerified && orderState !== OrderState.AUTHORIZED;
    }, [orderNo, paymentVerified, orderState]);

    const isOrderReadyToSubmit = useCallback(() => {
        return orderNo && !!paymentVerified && orderState === OrderState.AUTHORIZED;
    }, [orderNo, paymentVerified, orderState]);

    // for PayPal: poll order state, wait for order being updated after successful payment authorization
    useEffect(() => {
        if (isOrderPending()) {
            const interval = setInterval(() => {
                getOrderState(orderNo);
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [paymentFacility, paymentVerified, orderState, orderNo, getOrderState, isOrderPending, isPayPalProvider]);

    // for PayPal: automatically submit the order if payment and order are authorized
    useEffect(() => {
        if(isPayPalPayment() && isOrderReadyToSubmit()) {
            submitOrder();
        }
    }, [paymentFacility, paymentVerified, orderState, orderNo, submitOrder, isPayPalPayment, isOrderReadyToSubmit]);

    const paymentHint = (paymentMethod) => {
		switch (paymentMethod) {
            case PAYMENT_METHODS.FREE:
			case PAYMENT_METHODS.PREPAID:
			case PAYMENT_METHODS.CASH:
			case PAYMENT_METHODS.PAY_LATER:
				return "cart.processHint.step4.withoutAuthorization";
			default:
				return "cart.processHint.step4.withAuthorization";
		}
	};

	const toggleAGB = () => {
        if (!showLegalTerms) emitInternalEvent("text_terms_view");
        setShowLegalTerms(!showLegalTerms);
	};

    const toggleWarrantyNotice = () => {
        if (!showWarrantyNotice) emitInternalEvent("text_warranty_view");
        setShowWarrantyNotice(!showWarrantyNotice);
    };

    const toggleAccept = () => {
        setAccept(!accept);
    };

    const renderSelectedPayment = () => {
		switch (paymentFacility.paymentMethod) {
			case PAYMENT_METHODS.PAYPAL:
			case PAYMENT_METHODS.PAYPAL_EXPRESS:
				return <span title={t("cart.paymentMethods.paypal")} className={"hpm-paymentcarts hpm-paypal " + i18n.language.slice(0, 2)}/>;
			case PAYMENT_METHODS.CASH:
				return <span title={t("cart.paymentMethods.cash")} className={"hpm-paymentcarts hpm-euro " + i18n.language.slice(0, 2)}/>;
			case PAYMENT_METHODS.PREPAID:
				return <span title={t("cart.paymentMethods.prepaid")} className={"hpm-paymentcarts hpm-bankWire " + i18n.language.slice(0, 2)}/>;
			case PAYMENT_METHODS.PRZELEWY24:
				return <span title={t("cart.paymentMethods.przelewy24")} className={"hpm-paymentcarts hpm-przelewy24 " + i18n.language.slice(0, 2)}/>;
			case PAYMENT_METHODS.SEPA:
				return <span title={t("cart.paymentMethods.sepa")} className={"hpm-paymentcarts hpm-sepa " + i18n.language.slice(0, 2)}/>;
			case PAYMENT_METHODS.CARD:
				return <span title={t("cart.paymentMethods.card")} className={"hpm-paymentcarts hpm-card " + i18n.language.slice(0, 2)}/>;
			default:
				return null;
		}
	};

    const handlePayClicked = () => {
        setIsSubmitted(true);
        props.submitOrder();
    }

	const renderShippingAddress = () => {
		if (adress.shippingAdress) {
			return (
				<div className={"hpm-adressBox"}>
					<span className="hpm-title">{t("cart.personalInformation.shippingAdress")}</span>
					<span
						className={"hpm-edit"}
						onClick={() => props.editBillingAddress()}
						onKeyDown={e => {
							if (e.key === "Enter" || e.key === " ") {
                                props.editBillingAddress();
							}
						}}
						role="button"
						tabIndex={0}
					>
                        {t("cart.change")}
                    </span>
					<table>
						<tbody>
							<tr>
								<td>{adress.shippingName} {adress.shippingFamilyname}</td>
							</tr>
							<tr>
								<td>{adress.shippingStreet}</td>
							</tr>
							<tr>
								<td>{adress.shippingPostalCode} {adress.shippingCity}</td>
							</tr>
						</tbody>
					</table>
				</div>
			);
		}
	}

    const renderNextButton = () => {
        if(!accept) {
            return null;
        }
        const method = paymentFacility.paymentMethod;
        switch (method) {
            case PAYMENT_METHODS.FREE:
            case PAYMENT_METHODS.PREPAID:
            case PAYMENT_METHODS.CASH:
            case PAYMENT_METHODS.PAYPAL_EXPRESS:
                return (
                    <NextButton onClick={handlePayClicked}>
                        {t("cart.calculation.submit")}
                    </NextButton>
                );
            case PAYMENT_METHODS.PRZELEWY24:
                return (
                    <P24PayPalButtonContainer />
                );
            case PAYMENT_METHODS.PAY_LATER:
            case PAYMENT_METHODS.PAYPAL:
            case PAYMENT_METHODS.CARD:
            case PAYMENT_METHODS.SEPA:
                return (
                    <PayPalButtonContainer fundingSource={getFundingSource(method)}/>
                );
            default: return null;
        }
    }

    const modalStyles = {
        overlay: {
            zIndex: "100000"
        },
        content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid grey",
            width: "calc(90%)",
            maxWidth: "1000px",
            minHeight: "200px",
            maxHeight: "90vh",
            overflowY: "auto",
        }
    };

    const downloadButtonStyles = {
        display: "flex",
        justifyContent: "end",
        marginRight: "3em"
    };

    const isLoading = () => {

        // some complex logic due to different checkout processes
        // Vorkasse, Abholung, PayPalExpress: when pressed the Pay-Button, it is loading (waiting for /accept to return)
        if(isSubmitted) { return true}

        // PayPal, P24 (excl. PayPalExpress), order is pending or readyToSubmit, means it will be automatically submitted
        // when polling the order state returns "authorized", so it is loading
        if(isPayPalPayment() && (isOrderPending() || isOrderReadyToSubmit())) { return true}
    };

    if(isLoading()) {
        return (
            <React.Fragment>
                <div className={"hpm-loadingMessage"}>
                    <h3>{t("cart.checkingOrder")}</h3>
                </div>
                <Loading height={100} />
            </React.Fragment>
        );
    }

    return (
        <div className={"hpm-orderSummary"}>
            <div className={"hpm-cartContainer"}>
                <div className="hpm-mainContentBox">
                    <div className="hint" dangerouslySetInnerHTML={{ __html: t(paymentHint(paymentFacility.paymentMethod)) }}></div>
                    <div className={"hpm-cartTitle"}>{t("cart.cartLabel")}</div>
                    <ul className="hpm-cart">
                        {
                            items.map((item, index) => {
                                return <CartItemOverviewContainer key={index} cartItem={item}/>;
                            })
                        }
                    </ul>
                    <div className="hpm-box hpm-shipping">
                        <span
                            className="hpm-title">{t("cart.expectedDeliveryDate.expectedDeliveryDateLabel")}</span>
                        <table>
                            <tbody>
                                <tr>
                                    <td>{shippingText}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {showPickupLocation &&
                        <div className="hpm-box">
                            <span className="hpm-title">
                                {withPickup ? t("cart.articlePickupLocation") : t("cart.articleMountLocation")}
                            </span>
                            <div>
                                <PickupLocationInfoContainer/>
                            </div>
                        </div>
                    }
                    {(paymentFacility.paymentProvider && !paymentFacility.paymentProvider === PAYMENT_PROVIDER.FREE) &&
                        <div className="hpm-box hpm-payment">
                            <span
                                className="hpm-title">{t("cart.paymentMethods.selectedPaymentMethod")}
                            </span>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>
                                            {renderSelectedPayment()}
                                        </td>
                                    </tr>
                                    {paymentFacility.paymentProvider === PAYMENT_PROVIDER.PREPAID &&
                                    <React.Fragment>
                                        <tr>
                                            <td></td>
                                        </tr>
                                        <tr className={"hpm-payment-notice"}>
                                            <td>
                                                {t("cart.shipping.prepaidNotice")}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                }
                                </tbody>
                            </table>
                        </div>
                    }
                    {euShop &&
                        <div className="hpm-box hpm-warrantyRights" data-testid="warranty-rights">
                            <button
                                type="button"
                                onClick={toggleWarrantyNotice}
                            >
                                {t("warranty.notice.checkoutLink")}
                            </button>
                        </div>
                    }
                    <WarrantyNoticeModal isOpen={showWarrantyNotice} onRequestClose={toggleWarrantyNotice} t={t}/>
                    {legalTerms &&
                        <div className="hpm-box hpm-legalTerms">
                            {t("cart.calculation.legalTerms.infoTextPartOne")}
                            <a
                                href="#"
                                onClick={e => {
                                    e.preventDefault();
                                    toggleAGB();
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleAGB();
                                    }
                                }}
                            >
                                {t("cart.calculation.legalTerms.linkText")}
                            </a>
                            {t("cart.calculation.legalTerms.infoTextPartTwo")}
                        </div>
                    }
                    <Modal isOpen={showLegalTerms}
                           style={modalStyles}
                           shouldCloseOnOverlayClick={true}
                           onRequestClose={toggleAGB}
                    >
                        <button type={"button"} onClick={toggleAGB} className="hpm-modalClose"/>
                        <div style={downloadButtonStyles}>
                            <a className={"file-download-button"}
                               download={t("cart.calculation.legalTerms.fileName")}
                               id="file-download-button"
                               href={`${notificationUrl}/download/legalTerms/${dealerId}`}>{t("cart.calculation.legalTerms.button")}
                            </a>
                        </div>
                        <div dangerouslySetInnerHTML={{__html: legalTerms}}
                             className="hpm-modalContent" style={{fontFamily: "var(--font100)"}}/>
                    </Modal>
                </div>

                <div className="hpm-dataSummaryBox">
                    {!isPayPalExpressPayment() && // PP Express: we have no address data
                    <div className="hpm-box">
                        <div className={"hpm-adressBox"}>
                            <span
                                className="hpm-title">{t("cart.personalInformation.billingAdress")}</span>
                            <span
                                className={"hpm-edit"}
                                onClick={() => props.editBillingAddress()}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        props.editBillingAddress();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                            >
                                {t("cart.change")}
                            </span>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>
                                            {adress.name} {adress.familyname}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            {adress.street}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            {adress.postalCode} {adress.city}
                                        </td>
                                    </tr>
                                    {adress.phone
                                        ? <tr>
                                            <td>✆ {adress.phone}</td>
                                        </tr> : null
                                    }
                                    {adress.email
                                        ? <tr>
                                            <td>✉ {adress.email}</td>
                                        </tr> : null
                                    }
                                </tbody>
                            </table>
                        </div>
                        {renderShippingAddress()}
                    </div>}
                    <div className={"hpm-dataSummaryBoxContent"}>
                        <PriceOverviewContainer hideSlyCoupon={true} />
                        <div className="hpm-menu">
                            <div
                                className="hpm-menuElement hpm-conditions"
                                onClick={() => toggleAccept()}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        toggleAccept();
                                    }
                                }}
                                role="checkbox"
                                aria-checked={accept || false}
                                tabIndex={0}
                            >
                                <div className={accept ? "hpm-circle hpm-selected" : "hpm-circle"}/>
                                <div className="hpm-optionText">
                                    {t("cart.calculation.consent")}
                                </div>
                            </div>
                        </div>
                        <div className={"hpm-dataSummaryBoxButtons"}>
                            {renderNextButton()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSummary;
